import React, { useState, useMemo } from "react";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import { StatCard, LineChart, DonutChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { recoveryStats, recoveryTrends, recoveryDistribution, heartRatePatterns } from "../data";

export const Recovery: React.FC = () => {
  const theme = useTheme();
  const [trendView, setTrendView] = useState("recovery_score");

  const distributionData = useMemo(() => [
    { ...recoveryDistribution[0], color: theme.palette.success.main },
    { ...recoveryDistribution[1], color: theme.palette.warning.main },
    { ...recoveryDistribution[2], color: theme.palette.error.main },
  ], [theme]);

  const statCards = [
    { metric: recoveryStats.recovery_score, icon: FavoriteIcon },
    { metric: recoveryStats.hrv, icon: MonitorHeartIcon },
    { metric: recoveryStats.resting_heart_rate, icon: VolunteerActivismIcon },
    { metric: recoveryStats.sleep_performance, icon: BedtimeIcon },
  ];

  const getTrendLabel = () => {
    switch (trendView) {
      case "recovery_score": return "Recovery Score";
      case "hrv": return "Heart Rate Variability";
      case "resting_heart_rate": return "Resting Heart Rate";
      case "sleep_performance": return "Sleep Performance";
      default: return "Recovery Score";
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case "recovery_score":
      case "sleep_performance": return 100;
      case "hrv": return 200;
      case "resting_heart_rate": return 100;
      default: return 100;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Recovery Trends"
              subtitle="Daily trends showing recovery metrics over time."
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, v) => { if (v !== null) setTrendView(v); }}
                  size="small"
                >
                  <ToggleButton value="recovery_score">Recovery Score</ToggleButton>
                  <ToggleButton value="hrv">Heart Rate Variability</ToggleButton>
                  <ToggleButton value="resting_heart_rate">Resting Heart Rate</ToggleButton>
                  <ToggleButton value="sleep_performance">Sleep Performance</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <LineChart
                data={recoveryTrends}
                xAxisKey="label"
                series={[{ dataKey: trendView, label: getTrendLabel(), color: theme.palette.success.main, area: true }]}
                height={300}
                yAxisMin={0}
                yAxisMax={getYAxisMax()}
                showMinMaxLines
                referenceMin={trendView === "recovery_score" ? 22 : trendView === "hrv" ? 18 : trendView === "resting_heart_rate" ? 48 : 55}
                referenceMax={trendView === "recovery_score" ? 95 : trendView === "hrv" ? 85 : trendView === "resting_heart_rate" ? 72 : 92}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Recovery Score Distribution"
              subtitle="Proportion of employees in each recovery range."
            >
              <DonutChart data={distributionData} height={300} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Heart Rate Patterns by Time of Day"
              subtitle="Variation in heart rate patterns throughout the day."
            >
              <LineChart
                data={heartRatePatterns}
                xAxisKey="time"
                series={[{ dataKey: "value", label: "Heart Rate", color: theme.palette.warning.main, area: true }]}
                height={300}
                yAxisMin={0}
                showMinMaxLines
                referenceMin={52}
                referenceMax={95}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
